/**
 * Nhãn dùng chung cho các khối điểm, để hai bảng đứng cạnh nhau không gọi cùng
 * một thứ bằng hai tên khác nhau.
 *
 * Trước khi có file này, bảng "Điểm 3 năm THPT" hiện "Vật lí" (lấy nguyên văn từ
 * dữ liệu) còn khối Toán/Lý hiện "Lý" (viết cứng trong component) — cùng một môn,
 * hai tên, xếp ngay trên dưới nhau.
 */

/** Ba năm THPT, theo thứ tự cột của mọi bảng điểm. */
export const YEAR_LABELS = ["Lớp 10", "Lớp 11", "Lớp 12"] as const;

/** Tên chuẩn của môn, dùng khi hiển thị. */
export const SUBJECT_LABELS = {
	math: "Toán",
	physics: "Vật lý",
} as const;

/**
 * Các cách viết đã gặp trong dữ liệu nguồn, đưa về một tên chuẩn.
 *
 * Nguồn không nhất quán giữa các cột: `Diem_Lop10` viết "Vật lí" còn `VatLy_HB`
 * viết "Vật lý". Khoá của map là tên đã `trim()` và hạ chữ thường, nên không cần
 * liệt kê từng biến thể hoa/thường.
 */
const SUBJECT_ALIASES: Record<string, string> = {
	"vật lí": SUBJECT_LABELS.physics,
	"vật lý": SUBJECT_LABELS.physics,
	lý: SUBJECT_LABELS.physics,
	lí: SUBJECT_LABELS.physics,
	toán: SUBJECT_LABELS.math,
	"toán học": SUBJECT_LABELS.math,
};

/**
 * Tên môn chuẩn để hiển thị. Tên chưa có trong danh sách thì giữ NGUYÊN VĂN —
 * dữ liệu còn nhiều môn khác ("Tin học", "Hoá học"…) và đoán bừa một tên chuẩn
 * cho chúng sẽ sai theo cách khó phát hiện hơn là để nguyên.
 */
export function canonicalSubject(raw: string): string {
	return SUBJECT_ALIASES[raw.trim().toLowerCase()] ?? raw.trim();
}

/**
 * Tách chuỗi điểm theo từng năm của MỘT môn thành ba giá trị theo lớp 10/11/12.
 *
 *   "Toán L10: 8.90; Toán L11: 9.40; Toán L2: 9.50"  ->  ["8.90", "9.40", "9.50"]
 *
 * Nguồn gộp cả ba năm vào một ô văn bản, nên không tách thì cả chuỗi bị dump ra
 * màn hình và không xếp cột được với bảng học bạ ngay bên trên.
 *
 * Lớp lấy từ số sau chữ "L". `L2` trong ví dụ trên là lỗi đánh máy CÓ THẬT của
 * nguồn (đúng phải là `L12`), nên số nào không phải 10/11/12 thì rơi về vị trí
 * xuất hiện: phần tử thứ ba là lớp 12. Ưu tiên số đọc được hơn vị trí, để một
 * chuỗi thiếu năm giữa vẫn xếp đúng cột thay vì bị dồn lên.
 */
export function parsePerYearScores(raw: string | null | undefined): (string | null)[] {
	const byYear: (string | null)[] = [null, null, null];
	if (typeof raw !== "string" || raw.trim().length === 0) return byYear;

	const parts = raw.split(";").filter((part) => part.trim().length > 0);

	parts.forEach((part, position) => {
		const [head, ...rest] = part.split(":");
		const score = rest.join(":").trim();
		if (!score) return;

		const grade = Number(/L\s*(\d+)/i.exec(head ?? "")?.[1]);
		const index = grade >= 10 && grade <= 12 ? grade - 10 : position;
		if (index >= 0 && index < byYear.length) byYear[index] = score;
	});

	return byYear;
}
